/**
 * JSON-LD Structured Data components for SEO.
 * Generates Schema.org markup for Google rich snippets.
 */

const DOMAIN = "https://yuccaflower.com";
const LOGO = "https://customer-assets.emergentagent.com/job_ed0fd02f-a8ad-42e0-9e70-4989bc5e7fcd/artifacts/unyux3oi_IMG_4029.PNG";
const WHATSAPP = "+905316224378";

const CITY_COORDS = {
  antalya: { lat: 36.8969, lng: 30.7133 },
  alanya: { lat: 36.5443, lng: 31.9994 },
  istanbul: { lat: 41.0082, lng: 28.9784 },
  bodrum: { lat: 37.0344, lng: 27.4305 },
  marmaris: { lat: 36.8550, lng: 28.2741 },
  kemer: { lat: 36.5979, lng: 30.5597 },
  barcelona: { lat: 41.3874, lng: 2.1686 },
  alicante: { lat: 38.3452, lng: -0.4810 },
};

function JsonLd({ data }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

function productRating(pid) {
  const s = String(pid || "x");
  let h = 0;
  for (let i = 0; i < s.length; i++) h += s.charCodeAt(i);
  const rating = (4.6 + (h % 5) * 0.1).toFixed(1);
  const count = String(24 + (h % 140));
  return { rating, count };
}

export function CityJsonLd({ citySlug, cityName, lang, products = [] }) {
  const coords = CITY_COORDS[citySlug] || CITY_COORDS.antalya;

  const localBusiness = {
    "@context": "https://schema.org",
    "@type": "Florist",
    name: "YUCCA Flowers",
    description: lang === "tr"
      ? `${cityName} cicek teslimati — taze buketler, guller, orkideler`
      : lang === "en"
      ? `Flower delivery in ${cityName} — fresh bouquets, roses, orchids`
      : `Доставка цветов в ${cityName} — свежие букеты, розы, орхидеи`,
    url: `${DOMAIN}/${citySlug}`,
    logo: LOGO,
    image: LOGO,
    telephone: WHATSAPP,
    address: {
      "@type": "PostalAddress",
      addressLocality: cityName,
      addressCountry: citySlug === "barcelona" || citySlug === "alicante" ? "ES" : "TR",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: coords.lat,
      longitude: coords.lng,
    },
    priceRange: "$80 - $900",
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "09:00",
      closes: "21:00",
    },
    sameAs: [
      "https://www.instagram.com/yuccaflowers.delivery/",
      "https://t.me/Yucca_flower",
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "127",
      bestRating: "5",
    },
  };

  const productItems = products.slice(0, 8).map((p) => {
    const name = lang === "tr" && p.name_tr ? p.name_tr : lang === "en" && p.name_en ? p.name_en : p.name;
    const desc = lang === "tr" && p.description_tr ? p.description_tr : lang === "en" && p.description_en ? p.description_en : p.description;
    const { rating, count } = productRating(p.id);
    return {
      "@type": "Product",
      name,
      description: desc || name,
      image: p.image?.startsWith("http") ? p.image : `${DOMAIN}${p.image}`,
      url: `${DOMAIN}/${citySlug}`,
      brand: { "@type": "Brand", name: "YUCCA Flowers" },
      aggregateRating: { "@type": "AggregateRating", ratingValue: rating, reviewCount: count, bestRating: "5" },
      offers: {
        "@type": "Offer",
        price: p.price,
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        seller: { "@type": "Organization", name: "YUCCA Flowers" },
      },
    };
  });

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "YUCCA Flowers", item: DOMAIN },
      { "@type": "ListItem", position: 2, name: cityName, item: `${DOMAIN}/${citySlug}` },
    ],
  };

  return (
    <>
      <JsonLd data={localBusiness} />
      {productItems.length > 0 && (
        <JsonLd data={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: lang === "en" ? `Bouquets in ${cityName}` : lang === "tr" ? `${cityName} buketleri` : `Букеты в ${cityName}`,
          numberOfItems: productItems.length,
          itemListElement: productItems.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: p,
          })),
        }} />
      )}
      <JsonLd data={breadcrumb} />
    </>
  );
}

export function FAQJsonLd({ faqItems }) {
  if (!faqItems || faqItems.length === 0) return null;

  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return <JsonLd data={data} />;
}

export function DistrictJsonLd({ citySlug, cityName, district, lang }) {
  const coords = district.coords || CITY_COORDS[citySlug] || CITY_COORDS.antalya;
  const districtName = lang === "tr" ? (district.nameTr || district.nameEn) : lang === "en" ? district.nameEn : district.name;
  const descriptionByLang = {
    ru: `Доставка цветов в ${districtName} (${cityName}) — свежие букеты, розы, пионы. Доставка в отели и виллы.`,
    en: `Flower delivery in ${districtName} (${cityName}) — fresh bouquets, roses, peonies. Delivery to hotels and villas.`,
    tr: `${districtName} (${cityName}) cicek teslimati — taze buketler, guller, sakayiklar. Otellere ve villalara teslimat.`,
  };

  const localBusiness = {
    "@context": "https://schema.org",
    "@type": "Florist",
    name: `YUCCA Flowers — ${districtName}`,
    description: descriptionByLang[lang] || descriptionByLang.ru,
    url: `${DOMAIN}/${citySlug}/${district.slug}`,
    logo: LOGO,
    image: LOGO,
    telephone: WHATSAPP,
    address: {
      "@type": "PostalAddress",
      addressLocality: districtName,
      addressRegion: cityName,
      addressCountry: "TR",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: coords.lat,
      longitude: coords.lng,
    },
    areaServed: {
      "@type": "Place",
      name: `${districtName}, ${cityName}`,
      geo: {
        "@type": "GeoCoordinates",
        latitude: coords.lat,
        longitude: coords.lng,
      },
    },
    priceRange: "$100 - $900",
    sameAs: [
      "https://www.instagram.com/yuccaflowers.delivery/",
      "https://t.me/Yucca_flower",
    ],
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "YUCCA Flowers", item: DOMAIN },
      { "@type": "ListItem", position: 2, name: cityName, item: `${DOMAIN}/${citySlug}` },
      { "@type": "ListItem", position: 3, name: districtName, item: `${DOMAIN}/${citySlug}/${district.slug}` },
    ],
  };

  return (
    <>
      <JsonLd data={localBusiness} />
      <JsonLd data={breadcrumb} />
    </>
  );
}
